import LZString from 'lz-string';

import {
  createDefaultFilterMap,
  type DefaultSelectionOverride,
} from '../contexts/defaultFilters';
import {
  type Filter,
  type FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';

export type FilterGroupsPayload = [string, [FilterId, Filter][]][];

export interface MinimalGroupSpec {
  groupKey: string;
  filterIds: FilterId[];
  defaultSelections?: DefaultSelectionOverride;
}

export function buildFilterGroups(
  spec: MinimalGroupSpec[],
  options: {
    primaryFilterId: PrimaryFilterId;
    taxonomyConfig?: TaxonomyConfig;
  }
): FilterGroupsPayload {
  return spec.map((group, index) => {
    const defaultMap = createDefaultFilterMap(
      index === 0 ? options.primaryFilterId : undefined,
      options.taxonomyConfig,
      group.defaultSelections
    );
    const pairs: [FilterId, Filter][] = group.filterIds
      .map((id) => {
        let filter = defaultMap.get(id);
        if (filter !== undefined && group.defaultSelections) {
          const override =
            group.defaultSelections[id as keyof DefaultSelectionOverride];
          if (override !== undefined && filter.type === FilterType.TREE) {
            filter = { ...filter, selections: override };
          }
        }
        return filter !== undefined
          ? ([id, filter] as [FilterId, Filter])
          : null;
      })
      .filter((p): p is [FilterId, Filter] => p !== null);
    return [group.groupKey, pairs];
  });
}

export function parseFilterGroupsFromEncoded(
  encoded: string
): FilterGroupsPayload | null {
  const decompressed = LZString.decompressFromEncodedURIComponent(encoded);
  if (decompressed === null || decompressed === '') return null;
  try {
    const parsed = JSON.parse(decompressed) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed as FilterGroupsPayload;
  } catch {
    return null;
  }
}

export function encodeFilterGroups(groups: FilterGroupsPayload): string {
  return LZString.compressToEncodedURIComponent(JSON.stringify(groups));
}
